import * as y from "yup";
import { AdminTicketType, KYDTicketType } from "@common/interfaces/KYDTicket";
import dayjs from "dayjs";
import { KYDAdminEvent, KYDEvent } from "@common/interfaces/KYDEvent";
import { KYDEventDefaults } from "@common/interfaces/KYDOrganization";
import Constants from "../../../../../../../../common/interfaces/Constants";

export type Mode = "edit" | "create";

export enum PriceMode {
  SINGLE = "single",
  TIERED_ALLOCATION = "tiered_allocation",
}

const toDayJsDate = (timezone) => (date: Date | null) => {
  if (date === null) {
    return null;
  }

  return dayjs(date).toDate();
};

const getTicketTypeTierValuesSchema = (
  kydEvent: KYDEvent,
  { shift_quantity_on_expiration = false }
) => {
  const eventStartsAt = dayjs(kydEvent.start_at).toDate();
  const eventEndsAt = dayjs(kydEvent.end_at).toDate();
  const eventOnSaleAt = dayjs(kydEvent.on_sale_at || today).toDate();

  return y.object({
    hidden: y.bool().nullable().default(false),
    code: y.string().nullable().optional(),
    isNew: y.boolean(),
    id: y.string().optional(),
    status: y.string().optional(),
    display_options: y
      .object({
        delete_options: y.array().of(y.string()).optional(),
      })
      .nullable(),
    name: y.string().required("Name is required").label("Name"),
    totalSold: y
      .number()
      .optional()
      .transform((value) => (value ? parseInt(value, 10) : undefined)),
    originalQuantity: y
      .number()
      .optional()
      .transform((value) => (value ? parseInt(value, 10) : undefined)),
    quantity: y
      .number()
      .transform((value) => (value ? parseInt(value, 10) : undefined))
      .when(["totalSold"], ([totalSold], schema) => {
        if (totalSold) {
          return schema
            .required("Quantity is required")
            .min(totalSold, `Cannot be below amount sold (${totalSold})`)
            .max(
              Constants.MAX_TICKET_QUANTITY_PER_TICKET_TYPE,
              `Cannot have quantity greater than ${Constants.MAX_TICKET_QUANTITY_PER_TICKET_TYPE}`
            );
        }

        return schema
          .required("Quantity is required")
          .min(0)
          .max(
            Constants.MAX_TICKET_QUANTITY_PER_TICKET_TYPE,
            `Cannot have quantity greater than ${Constants.MAX_TICKET_QUANTITY_PER_TICKET_TYPE}`
          );
      }),
    price: y
      .number()
      .transform((value) => (value ? parseInt(value, 10) : undefined))
      .required("Price is required"),
    limit: y
      .number()
      .transform((value) => (value ? parseInt(value, 10) : undefined))
      .max(100)
      .optional(),
    next_tier_id: y.string().optional(),
    sold_out: y.boolean().notRequired(),
    shift_quantity_on_expiration: y
      .boolean()
      .default(shift_quantity_on_expiration),
    expires_at: y
      .date()
      .nullable()
      //.transform(toDayJsDate(kydEvent.timezone))
      .when(["shift_quantity_on_expiration"], ([moveTickets], schema) => {
        if (moveTickets) {
          return schema
            .min(eventOnSaleAt, "Needs to be earlier than off sale date")
            .max(eventEndsAt, "Needs to be before the event ends");
        }

        return schema.default(undefined);
      }),
    on_sale_at: y
      .date()
      .nullable()
      .transform(toDayJsDate(kydEvent.timezone))
      .test(
        "min",
        `[Tier] On sale date needs to be after event on sale date.`,
        function (value) {
          if (!value) return true;
          return (
            dayjs(value).isSame(eventOnSaleAt, "minute") ||
            dayjs(value).isAfter(eventOnSaleAt, "minute")
          );
        }
      )
      .max(eventStartsAt, "Needs to be before end of the event."),
    off_sale_at: y
      .date()
      .nullable()
      .transform(toDayJsDate(kydEvent.timezone))
      .test(
        "min",
        "Off sale date needs to be after event on sale date.",
        function (value) {
          if (!value) return true;
          return (
            dayjs(value).isSame(eventOnSaleAt, "minute") ||
            dayjs(value).isAfter(eventOnSaleAt, "minute")
          );
        }
      )
      .max(eventEndsAt, "Needs to be before the end of the event"),
  });
};

const today = new Date();
const defaultTicketTypeValuesSchema = y.object({
  price_mode: y.mixed<PriceMode>().oneOf(Object.values(PriceMode)).required(),
  id: y.string().optional(),
  name: y.string().optional(),
  description: y.string(),
  sold_out: y.boolean().optional(),
  totalSold: y
    .number()
    .optional()
    .transform((value) => (value ? parseInt(value, 10) : undefined)),
  originalQuantity: y
    .number()
    .optional()
    .transform((value) => (value ? parseInt(value, 10) : undefined)),
  limit: y
    .number()
    .transform((value) => (value ? parseInt(value, 10) : undefined))
    .max(100)
    .required(),
  waitlist_status: y
    .mixed()
    .oneOf(["ENABLED", "DISABLED", "OPEN"])
    .default("DISABLED")
    .required(),
  waitlist_return: y
    .mixed()
    .oneOf(["ENABLED", "DISABLED"])
    .default("ENABLED")
    .required(),
});

interface ITicketTypeValues {
  start_at: KYDEvent["start_at"];
  end_at: KYDEvent["end_at"];
  on_sale_at: KYDEvent["on_sale_at"];
  off_sale_at: KYDEvent["off_sale_at"];
  timezone: KYDEvent["timezone"];
}

const validateBetweenDates = (start: Date, end: Date, message: string) => {
  return {
    name: "is-valid",
    test: (_date) => {
      const date = dayjs(_date);
      date.set("seconds", 0);

      if (date.isBefore(start, "minutes")) {
        console.log({
          date: _date,
          start,
          message: `${date} isBefore ${start}`,
        });
        return false;
      }

      if (date.isAfter(end, "minutes")) {
        console.log({
          date: _date,
          end,
          message: `${date} isAfter ${end}`,
        });
        return false;
      }

      return true;
    },
    message: () => message,
  };
};

export const getTicketTypeValuesSchema = (data: ITicketTypeValues) => {
  const eventStartsAt = dayjs(data.start_at).toDate();
  const eventEndsAt = dayjs(data.end_at).toDate();
  const eventOnSaleAt = dayjs(data.on_sale_at).toDate();
  const eventOffSaleAt = dayjs(data.off_sale_at || data.end_at).toDate();

  eventOnSaleAt.setSeconds(0);
  eventOffSaleAt.setSeconds(0);
  eventStartsAt.setSeconds(0);
  eventEndsAt.setSeconds(0);

  return defaultTicketTypeValuesSchema.shape({
    on_sale_at: y
      .date()
      .nullable()
      .transform(toDayJsDate(data.timezone))
      .test({
        name: "is-valid-on-sale",
        test: function (value) {
          if (!value) return true;

          const date = dayjs(value);
          date.set("seconds", 0);

          // Check against off_sale_at from current form values
          const offSaleAt = this.parent.off_sale_at;
          if (offSaleAt) {
            const offSaleDate = dayjs(offSaleAt);
            offSaleDate.set("seconds", 0);

            if (date.isAfter(offSaleDate, "minutes")) {
              return this.createError({
                path: "on_sale_at",
                message: `[On sale at] Must be before off sale date.`,
              });
            }
          }

          return true;
        },
      }),
    off_sale_at: y
      .date()
      .nullable()
      .transform(toDayJsDate(data.timezone))
      .test({
        name: "is-valid-off-sale",
        test: function (value) {
          if (!value) return true;

          const date = dayjs(value);
          date.set("seconds", 0);

          // Check against on_sale_at from current form values
          const onSaleAt = this.parent.on_sale_at;
          if (onSaleAt) {
            const onSaleDate = dayjs(onSaleAt);
            onSaleDate.set("seconds", 0);

            if (date.isBefore(onSaleDate, "minutes")) {
              return this.createError({
                path: "off_sale_at",
                message: `[Off sale at] Must be after on sale date.`,
              });
            }
          }

          return true;
        },
      }),
    start_at: y
      .date()
      .nullable()
      .transform(toDayJsDate(data.timezone))
      .test(
        validateBetweenDates(
          eventOnSaleAt,
          eventEndsAt,
          `[Start at] Must be after event on sale and before event ends.`
        )
      ),
    end_at: y
      .date()
      .nullable()
      .transform(toDayJsDate(data.timezone))
      .test(
        validateBetweenDates(
          eventOnSaleAt,
          eventEndsAt,
          `[End at] Must be after event on sale and before event ends.`
        )
      ),
    hidden: y.boolean().optional().nullable().default(false),
    code: y.string().nullable().optional(),
    waitlist_status: y.string().nullable().optional(),
    waitlist_return: y.string().nullable().optional(),
    increment: y
      .number()
      .transform((value) => (value ? parseInt(value, 10) : undefined))
      .default(1)
      .optional(),
    quantity: y.number().when("price_mode", {
      is: PriceMode.SINGLE,
      then: (schema) =>
        schema
          .min(0)
          .required()
          .transform((value) => (value ? parseInt(value) : 0))
          .when(["totalSold"], ([totalSold], s) => {
            if (typeof totalSold === "number") {
              return s.min(
                totalSold,
                `Cannot be below amount sold (${totalSold})`
              );
            }
            return s;
          })
          .max(
            Constants.MAX_TICKET_QUANTITY_PER_TICKET_TYPE,
            `Cannot have quantity greater than ${Constants.MAX_TICKET_QUANTITY_PER_TICKET_TYPE}`
          ),
      otherwise: (schema) =>
        schema.transform((value) => (value ? parseInt(value) : 0)).optional(),
    }),
    free: y.boolean().optional(),
    price: y
      .string()
      .when("free", {
        is: true,
        then: (schema) => schema.optional(),
        otherwise: (schema) => schema.required(),
      })
      .when("price_mode", {
        is: PriceMode.TIERED_ALLOCATION,
        then: (schema) => schema.notRequired(),
      }),
    shift_quantity_on_expiration: y.boolean().default(false),
    tiers: y.array().test((options, { createError, resolve, path, parent }) => {
      const safeOptions = options || [];
      let errors = [];
      // let error = null;
      let lastDate = safeOptions[0]?.expires_at;
      safeOptions.forEach((t, index) => {
        const previous = dayjs(lastDate);

        if (!t.name) {
          errors.push(
            createError({
              path: `${path}.${index}.name`,
              message: `Name is required`,
              type: "required",
            })
          );
        }

        if (isNaN(t.quantity)) {
          errors.push(
            createError({
              path: `${path}.${index}.quantity`,
              message: `Quantity is required`,
              type: "required",
            })
          );
        }

        if (!t.price) {
          errors.push(
            createError({
              path: `${path}.${index}.price`,
              message: `Price is required`,
              type: "required",
            })
          );
        }

        const quantity_int = parseInt(t.quantity, 10);
        if (!isNaN(quantity_int)) {
          if (typeof t.totalSold === "number" && quantity_int < t.totalSold) {
            errors.push(
              createError({
                path: `${path}.${index}.quantity`,
                message: `Cannot be below amount sold (${t.totalSold})`,
              })
            );
          }

          if (quantity_int > Constants.MAX_TICKET_QUANTITY_PER_TICKET_TYPE) {
            errors.push(
              createError({
                path: `${path}.${index}.quantity`,
                message: `Cannot have quantity greater than ${Constants.MAX_TICKET_QUANTITY_PER_TICKET_TYPE}`,
              })
            );
          }
        }

        const isOffSale = t.status === "OFFSALE";
        const isEdit = !!t.id;
        if (!t.quantity && !isOffSale && !isEdit) {
          errors.push(
            createError({
              path: `${path}.${index}.quantity`,
              message: `Quantity is required`,
              type: "required",
            })
          );
        }

        if (t.on_sale_at && t.off_sale_at) {
          if (dayjs(t.on_sale_at).isAfter(dayjs(t.off_sale_at))) {
            errors.push(
              createError({
                path: `${path}.${index}.on_sale_at`,
                message: `On sale date needs to be before off sale date`,
              })
            );

            errors.push(
              createError({
                path: `${path}.${index}.off_sale_at`,
                message: `Off sale date needs to be after on sale date`,
              })
            );
          }
        }

        if (t.on_sale_at) {
          if (dayjs(t.on_sale_at).isBefore(eventOnSaleAt, "minute")) {
            errors.push(
              createError({
                path: `${path}.${index}.on_sale_at`,
                message: `[Tier ${
                  index + 1
                }] On sale date needs to be after event on sale date`,
              })
            );
          } else if (dayjs(t.on_sale_at).isAfter(eventStartsAt, "minute")) {
            errors.push(
              createError({
                path: `${path}.${index}.on_sale_at`,
                message: `On sale date needs to be before event start date`,
              })
            );
          }
        }

        if (t.off_sale_at) {
          if (dayjs(t.off_sale_at).isAfter(eventEndsAt, "minute")) {
            errors.push(
              createError({
                path: `${path}.${index}.off_sale_at`,
                message: `Off sale date needs to be before event end date`,
              })
            );
          } else if (dayjs(t.off_sale_at).isBefore(eventOnSaleAt, "minute")) {
            errors.push(
              createError({
                path: `${path}.${index}.off_sale_at`,
                message: `Off sale date needs to be after event on sale date`,
              })
            );
          }
        }

        if (!parent.shift_quantity_on_expiration) {
          return;
        }
        const isLast = index === options.length - 1;

        if (!t.expires_at && !isLast) {
          errors.push(
            createError({
              path: `${path}.${index}.expires_at`,
              message: `Expires at is required`,
              type: "required",
            })
          );
        }

        const current = dayjs(t.expires_at);
        // Set it for next iteration
        lastDate = t.expires_at;

        if (current.isBefore(previous)) {
          errors.push(
            createError({
              params: {},
              path: `${path}.${index}.expires_at`,
              message: `Tier ${
                index + 1
              } expires at can't be before the previous tier`,
            })
          );
        }

        if (
          !previous.isSame(current, "minute") &&
          current.diff(previous, "hour") < 1 &&
          t.pool?.version === "v1"
        ) {
          errors.push(
            createError({
              path: `${path}.${index}.expires_at`,
              message: `Tiers must have at least an hour difference`,
            })
          );
        }
      });

      // Add a global error message if tiers are not in chronological order
      if (parent.price_mode === PriceMode.TIERED_ALLOCATION) {
        const tiers = safeOptions || [];
        let outOfOrder = false;
        let previousDate = null;

        for (let i = 0; i < tiers.length; i++) {
          const currentExpiry = tiers[i].expires_at;

          // Skip empty expiry dates
          if (!currentExpiry) continue;

          // If we have a previous date to compare with
          if (
            previousDate &&
            dayjs(currentExpiry).isBefore(dayjs(previousDate))
          ) {
            outOfOrder = true;
            break;
          }

          previousDate = currentExpiry;
        }

        if (outOfOrder) {
          errors.push(
            createError({
              path: path,
              message:
                "Tiers must be in chronological order by expiration date",
            })
          );
          // Add errors to all date components for visual indication
          for (let i = 0; i < tiers.length; i++) {
            if (tiers[i].expires_at) {
              errors.push(
                createError({
                  path: `${path}.${i}.expires_at`,
                  // putting a space to avoid the default error message
                  message: " ",
                })
              );
            }
          }
        }
      }

      return errors.length ? new y.ValidationError(errors) : true;
    }),
  });
};

export type TicketTypeValues = y.InferType<
  ReturnType<typeof getTicketTypeValuesSchema>
>;
export type TicketTypeTier = y.InferType<
  ReturnType<typeof getTicketTypeTierValuesSchema>
>;

const makeNullDelete = (obj: object): TicketTypeValues => {
  return Object.keys(obj).reduce((finalObj, key) => {
    let value = obj[key];

    if (value === null) {
      value = "_DELETE_";
    } else if (typeof value === "object" && !Array.isArray(value)) {
      value = makeNullDelete(value);
    } else if (Array.isArray(value)) {
      value = value.map((item) => makeNullDelete(item));
    }

    return {
      ...finalObj,
      [key]: value,
    };
  }, {});
};

const toTier = (
  ticketType: AdminTicketType,
  timezone: string
): TicketTypeTier => ({
  id: ticketType.id,
  name: ticketType.name || "",
  limit: ticketType.limit,
  code: ticketType.code,
  hidden: ticketType.hidden,
  // next_tier_id: ticketType.next_tier_id,
  price: ticketType.price.units,
  quantity: ticketType.quantity,
  originalQuantity: ticketType.quantity, // used for validation
  sold_out: ticketType.sold_out,
  status: ticketType.status,
  totalSold: ticketType.quantity - ticketType.remaining,
  display_options: ticketType.display_options,
  shift_quantity_on_expiration: ticketType.shift_quantity_on_expiration,
  expires_at: ticketType.expires_at
    ? dayjs.utc(ticketType.expires_at).toDate()
    : null,
  on_sale_at: ticketType.on_sale_at
    ? dayjs.utc(ticketType.on_sale_at).toDate()
    : null,
  off_sale_at: ticketType.off_sale_at
    ? dayjs.utc(ticketType.off_sale_at).toDate()
    : null,
});

export function getTiers(
  ticketType: AdminTicketType,
  data: Pick<KYDAdminEvent, "tiered_ticket_types" | "timezone">,
  tiers: TicketTypeTier[] = []
): TicketTypeTier[] {
  if (!data || !ticketType) return [];

  if (ticketType.tier !== undefined) {
    const nextTier = data.tiered_ticket_types.find(
      (t) => t.id === ticketType.next_tier_id
    );

    if (!nextTier) {
      return tiers.concat(toTier(ticketType, data.timezone));
    }

    return getTiers(
      nextTier,
      data,
      tiers.concat(toTier(ticketType, data.timezone))
    );
  }

  return [];
}

const toDateIfNotUndefined = (
  date: Date | null | undefined,
  timezone: string
) => {
  if (date === null) return date;

  if (typeof date === "undefined") return undefined;

  console.log(date);

  const dateToReturn = dayjs(date);

  if (dateToReturn.isValid()) {
    return dateToReturn.toISOString();
  }

  return null;
};

export const transform = {
  input: (
    ticketType: AdminTicketType,
    data: Pick<KYDAdminEvent, "tiered_ticket_types" | "timezone">,
    event_defaults?: KYDEventDefaults
  ): TicketTypeValues => {
    if (!ticketType) {
      // Default values
      return {
        code: undefined,
        price_mode: PriceMode.SINGLE,
        description: undefined,
        name: undefined,
        free: undefined,
        price: undefined,
        hidden: false,
        limit: undefined,
        tiers: [],
        quantity: undefined,
        sold_out: false,
        totalSold: undefined,
        originalQuantity: undefined,
        start_at: null,
        end_at: null,
        on_sale_at: null,
        off_sale_at: null,
        waitlist_status: event_defaults?.waitlist_status || "DISABLED",
        waitlist_return: event_defaults?.waitlist_return || "ENABLED",
        shift_quantity_on_expiration: false,
      };
    }

    const { timezone } = data;
    const {
      name = "",
      description = "",
      end_at,
      start_at,
      on_sale_at,
      off_sale_at,
      code = null,
      hidden,
      price,
      quantity,
      limit,
      next_tier_id,
      pool,
      increment = 1,
    } = ticketType;

    const tiers = getTiers(ticketType, data);
    return {
      id: ticketType.id,
      name,
      description,
      end_at: end_at ? dayjs(end_at).toDate() : null,
      start_at: start_at ? dayjs(start_at).toDate() : null,
      on_sale_at: on_sale_at ? dayjs(on_sale_at).toDate() : null,
      off_sale_at: off_sale_at ? dayjs(off_sale_at).toDate() : null,
      code,
      price: price.units.toString(),
      quantity,
      increment,
      hidden,
      limit,
      sold_out: ticketType.sold_out,
      totalSold: ticketType.quantity - ticketType.remaining,
      originalQuantity: ticketType.quantity,
      shift_quantity_on_expiration: ticketType.shift_quantity_on_expiration,
      price_mode: pool ? PriceMode.TIERED_ALLOCATION : PriceMode.SINGLE,
      waitlist_status: ticketType.waitlist_status,
      waitlist_return: ticketType.waitlist_return,
      tiers,
    };
  },
  output: (values: TicketTypeValues, mode: Mode, timezone?: string) => {
    if (mode === "create") {
      return values;
    }

    if (mode === "edit") {
      // const data = makeNullDelete(values);
      const { end_at, start_at, on_sale_at, off_sale_at, tiers = [] } = values;

      const fixedTiers = tiers.map(({ display_options: _, ...t }) => {
        if (t.id?.includes("_CREATE_")) {
          return {
            ...t,
            id: "_CREATE_",
          };
        }

        return t;
      });

      return makeNullDelete({
        id: values.id,
        ...values,
        tiers: fixedTiers.length ? fixedTiers : undefined,
        end_at: toDateIfNotUndefined(end_at, timezone),
        start_at: toDateIfNotUndefined(start_at, timezone),
        on_sale_at: toDateIfNotUndefined(on_sale_at, timezone),
        off_sale_at: toDateIfNotUndefined(off_sale_at, timezone),
      });
    }
  },
};
